import React, { useState, useEffect } from 'react';
import {
  ShieldCheck, AlertTriangle, XCircle, RefreshCw, X, CheckCircle2,
  Calendar, Info, ArrowUpRight, Database
} from 'lucide-react';
import { fetchMacroAuditStatus, runMacroAudit, MacroAuditResponse } from '../services/macroAuditService';

interface MacroIndicatorAuditModalProps {
  isOpen: boolean;
  onClose: () => void;
  language?: string;
  marketMode?: 'US' | 'KR';
  indicators?: any;
}

export function MacroIndicatorAuditModal({
  isOpen,
  onClose,
  language = 'ko',
  marketMode = 'US',
  indicators
}: MacroIndicatorAuditModalProps) {
  const [loading, setLoading] = useState<boolean>(false);
  const [running, setRunning] = useState<boolean>(false);
  const [auditData, setAuditData] = useState<MacroAuditResponse | null>(null);
  const [filter, setFilter] = useState<'ALL' | 'FAIL' | 'WARN' | 'PASS'>('ALL');

  const lang = language || 'ko';

  const loadData = async () => {
    setLoading(true);
    try {
      // indicators가 전달되었거나 이전 검증 결과가 0%인 경우 즉시 실시간 정밀 검증 실행
      if (indicators && Object.keys(indicators).length > 0) {
        const runRes = await runMacroAudit(indicators);
        setAuditData(runRes);
      } else {
        const data = await fetchMacroAuditStatus('ALL');
        if (!data.audits || data.audits.length === 0 || data.stats?.accuracyRate === 0) {
          const runRes = await runMacroAudit(indicators);
          setAuditData(runRes);
        } else {
          setAuditData(data);
        }
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      loadData();
    }
  }, [isOpen, indicators]);

  const handleRunAudit = async () => {
    setRunning(true);
    try {
      const res = await runMacroAudit(indicators);
      setAuditData(res);
    } catch (err) {
      console.error('Audit execution error:', err);
    } finally {
      setRunning(false);
    }
  };

  if (!isOpen) return null;

  const rawList = auditData?.results || auditData?.audits || [];
  // Normalize items
  const items = rawList.map((item: any) => ({
    id: item.indicator_id || item.id,
    nameKo: item.nameKo || item.indicator_id || item.id,
    nameEn: item.nameEn || item.indicator_id || item.id,
    market: item.market || 'US',
    cachedVal: item.cached_val !== undefined ? item.cached_val : item.cachedVal,
    status: item.status || 'PASS',
    issueType: item.issue_type || item.issueType || 'NONE',
    message: item.message || '',
    indicatorDate: item.indicator_date || item.indicatorDate,
    releaseDate: item.release_date || item.releaseDate,
    auditedAt: item.audited_at || item.auditedAt
  }));

  const filteredItems = items.filter((item: any) => {
    if (filter === 'ALL') return true;
    return item.status === filter;
  });

  const stats = auditData?.stats || {
    totalCount: items.length,
    passCount: items.filter(i => i.status === 'PASS').length,
    warnCount: items.filter(i => i.status === 'WARN').length,
    failCount: items.filter(i => i.status === 'FAIL').length,
    accuracyRate: items.length > 0 ? Math.round((items.filter(i => i.status === 'PASS').length / items.length) * 100) : 100
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-fade-in">
      <div className="bg-slate-900 border border-white/10 rounded-3xl w-full max-w-4xl max-h-[90vh] flex flex-col shadow-2xl overflow-hidden">
        
        {/* Modal Header */}
        <div className="p-6 border-b border-white/10 flex items-center justify-between bg-white/[0.02]">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-400">
              <ShieldCheck className="w-6 h-6" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-lg font-bold text-white">
                  {lang === 'ko' ? '매크로 레짐 지표 무결성 검증 센터' : 'Macro Indicators Integrity Audit Center'}
                </h3>
                <span className="px-2 py-0.5 text-[10px] font-bold rounded-full bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">
                  {lang === 'ko' ? '공식 원천 대조' : 'Source Verified'}
                </span>
              </div>
              <p className="text-xs text-white/50 mt-0.5">
                {lang === 'ko'
                  ? 'FRED, 한국은행 ECOS, Yahoo Finance 지표의 이상치·단위·발표지연 여부를 실시간 감사(Audit)합니다.'
                  : 'Automated sanity, spike, freshness, and unit checks against FRED, ECOS, and Yahoo Finance.'}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={handleRunAudit}
              disabled={running || loading}
              className="flex items-center gap-2 px-3.5 py-2 rounded-xl text-xs font-semibold bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300 border border-emerald-500/30 transition-all disabled:opacity-50"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${running ? 'animate-spin' : ''}`} />
              <span>{running ? (lang === 'ko' ? '검증 실행 중...' : 'Auditing...') : (lang === 'ko' ? '지금 즉시 재검증' : 'Run Audit Now')}</span>
            </button>
            <button
              onClick={onClose}
              className="p-2 text-white/40 hover:text-white rounded-xl hover:bg-white/10 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Audit Stats Banner */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 p-6 bg-white/[0.01] border-b border-white/5">
          <div className="bg-white/5 border border-white/5 p-3.5 rounded-2xl">
            <div className="text-[11px] text-white/40 font-medium">{lang === 'ko' ? '검증 일치율' : 'Accuracy Rate'}</div>
            <div className="text-2xl font-black text-emerald-400 mt-0.5">{stats.accuracyRate}%</div>
            <div className="text-[10px] text-white/30 mt-1 flex items-center gap-1">
              <CheckCircle2 className="w-3 h-3 text-emerald-400" />
              <span>{lang === 'ko' ? '신뢰도 최고 등급' : 'High Reliability'}</span>
            </div>
          </div>

          <div className="bg-white/5 border border-white/5 p-3.5 rounded-2xl">
            <div className="text-[11px] text-white/40 font-medium">{lang === 'ko' ? '정상 수치 (PASS)' : 'Passed'}</div>
            <div className="text-2xl font-black text-white mt-0.5">{stats.passCount} <span className="text-xs text-white/40 font-normal">/ {stats.totalCount}</span></div>
            <div className="text-[10px] text-emerald-400/80 mt-1">{lang === 'ko' ? '허용 오차 범위 내' : 'Within Tolerance'}</div>
          </div>

          <div className="bg-white/5 border border-white/5 p-3.5 rounded-2xl">
            <div className="text-[11px] text-white/40 font-medium">{lang === 'ko' ? '주의/지연 (WARN)' : 'Warnings'}</div>
            <div className={`text-2xl font-black mt-0.5 ${stats.warnCount > 0 ? 'text-amber-400' : 'text-white/40'}`}>{stats.warnCount}</div>
            <div className="text-[10px] text-white/30 mt-1">{lang === 'ko' ? '갱신 지연 또는 변동성' : 'Stale / High Vol'}</div>
          </div>

          <div className="bg-white/5 border border-white/5 p-3.5 rounded-2xl">
            <div className="text-[11px] text-white/40 font-medium">{lang === 'ko' ? '이상치 오류 (FAIL)' : 'Failed'}</div>
            <div className={`text-2xl font-black mt-0.5 ${stats.failCount > 0 ? 'text-red-400' : 'text-white/40'}`}>{stats.failCount}</div>
            <div className="text-[10px] text-white/30 mt-1">{lang === 'ko' ? '단위/경계값 초과' : 'Out of Bounds'}</div>
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="flex items-center gap-2 px-6 pt-4 pb-2 border-b border-white/5">
          <span className="text-xs font-semibold text-white/40 mr-2">{lang === 'ko' ? '상태 필터:' : 'Filter:'}</span>
          {(['ALL', 'FAIL', 'WARN', 'PASS'] as const).map(tab => (
            <button
              key={tab}
              onClick={() => setFilter(tab)}
              className={`px-3 py-1 rounded-lg text-xs font-bold transition-all ${
                filter === tab
                  ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                  : 'text-white/40 hover:text-white hover:bg-white/5'
              }`}
            >
              {tab === 'ALL' ? (lang === 'ko' ? '전체 지표' : 'All') : tab}
            </button>
          ))}
          <div className="ml-auto text-[11px] text-white/30">
            {auditData?.lastAuditedAt && `${lang === 'ko' ? '최종 검증' : 'Audited'}: ${new Date(auditData.lastAuditedAt).toLocaleString()}`}
          </div>
        </div>

        {/* Indicator Audit Table */}
        <div className="flex-1 overflow-y-auto p-6 space-y-2.5">
          {loading ? (
            <div className="py-20 flex flex-col items-center justify-center gap-3">
              <RefreshCw className="w-6 h-6 text-emerald-400 animate-spin" />
              <p className="text-xs text-white/50">{lang === 'ko' ? '지표 무결성 검증 데이터를 불러오는 중...' : 'Loading audit results...'}</p>
            </div>
          ) : filteredItems.length === 0 ? (
            <div className="py-16 text-center text-white/40 text-xs">
              {lang === 'ko' ? '해당 상태의 지표가 없습니다.' : 'No indicators found for this filter.'}
            </div>
          ) : (
            filteredItems.map((item: any) => {
              const isPass = item.status === 'PASS';
              const isWarn = item.status === 'WARN';
              const isFail = item.status === 'FAIL';

              return (
                <div
                  key={item.id}
                  className="bg-white/5 hover:bg-white/[0.08] border border-white/5 rounded-2xl p-4 transition-all flex flex-col sm:flex-row sm:items-center justify-between gap-3"
                >
                  <div className="flex items-start gap-3">
                    <div className={`p-2 rounded-xl mt-0.5 ${
                      isPass ? 'bg-emerald-500/10 text-emerald-400' :
                      isWarn ? 'bg-amber-500/10 text-amber-400' : 'bg-red-500/10 text-red-400'
                    }`}>
                      {isPass ? <CheckCircle2 className="w-4 h-4" /> :
                       isWarn ? <AlertTriangle className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
                    </div>

                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-white text-sm">
                          {lang === 'ko' ? item.nameKo : item.nameEn}
                        </span>
                        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${item.market === 'KR' ? 'bg-blue-500/20 text-blue-300' : 'bg-purple-500/20 text-purple-300'}`}>
                          {item.market}
                        </span>
                        <span className="text-[10px] text-white/30 font-mono">[{item.id}]</span>
                      </div>
                      <p className={`text-xs mt-1 ${isPass ? 'text-white/60' : isWarn ? 'text-amber-300/80' : 'text-red-300 font-medium'}`}>
                        {item.message}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-4 sm:self-center self-end border-t sm:border-t-0 pt-2 sm:pt-0 border-white/5">
                    <div className="text-right">
                      <div className="text-[10px] text-white/40">{lang === 'ko' ? '시스템 수치' : 'Cached Val'}</div>
                      <div className="text-sm font-black text-white">
                        {item.cachedVal !== null && item.cachedVal !== undefined ? item.cachedVal : '-'}
                      </div>
                    </div>

                    <div className="text-right">
                      <div className="text-[10px] text-white/40">{lang === 'ko' ? '발표/기준일' : 'Release Date'}</div>
                      <div className="text-xs text-white/60 font-mono">
                        {item.releaseDate || item.indicatorDate || '-'}
                      </div>
                    </div>

                    <div className={`px-2.5 py-1 rounded-lg text-xs font-black tracking-wider ${
                      isPass ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' :
                      isWarn ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30' :
                      'bg-red-500/20 text-red-400 border border-red-500/30'
                    }`}>
                      {item.status}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Modal Footer */}
        <div className="p-4 border-t border-white/10 flex items-center justify-between text-xs text-white/40 bg-white/[0.01]">
          <div className="flex items-center gap-2">
            <Info className="w-4 h-4 text-emerald-400" />
            <span>{lang === 'ko' ? '모든 지표는 매일 정기 크론 및 온디맨드 검증을 통해 4단계 무결성을 검증합니다.' : 'Continuous automated audit pipeline keeps metrics accurate and reliable.'}</span>
          </div>
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-xl bg-white/10 hover:bg-white/20 text-white font-medium transition-colors"
          >
            {lang === 'ko' ? '닫기' : 'Close'}
          </button>
        </div>

      </div>
    </div>
  );
}
